import React from 'react';
import { ErrorMessage } from '@hookform/error-message';
import identity from 'lodash/identity';
import { get, useFormContext } from 'react-hook-form';

import { Box } from '@src/components/Box';
import { HStack } from '@src/components/HStack';
import { Icon } from '@src/components/Icon';
import { InfoBadge } from '@src/components/InfoBadge';
import { Label, Text } from '@src/components/Typography';
import { getTestId } from '@src/utils/testIds';
import {
  ListMultiSelectFieldProps,
  ListOptionType,
  MultiSelectFieldControllerProps,
} from './interface';

const renderTagDefault = (option: ListOptionType) =>
  option.tagProps ? <InfoBadge {...option.tagProps} /> : null;

const InternalMultiSelectList = (
  props: ListMultiSelectFieldProps & MultiSelectFieldControllerProps,
) => {
  const {
    formatOnChange = identity,
    onChange,
    name,
    options,
    parseValue = identity,
    value: fieldValue,
    renderTag = renderTagDefault,
  } = props;
  const {
    formState: { errors },
  } = useFormContext();

  const fieldErrors = get(errors, name);

  const parsedErrors = fieldErrors ? { [name]: fieldErrors } : {};

  const value = parseValue(fieldValue);

  const values: ListOptionType[] = value
    .filter((v: any) => options.some((o) => o.value === v))
    .map((v: any) => options.find((o) => o.value === v));

  return (
    <Box data-testid={getTestId(props)}>
      {options?.map((option) => {
        const selected = values.some((v) => v.value === option.value);

        const handleChange = () => {
          const newValue = option.value;
          const isNewValueSelected = values.some((v) => v.value === newValue);
          if (isNewValueSelected) {
            const mappedValues = [...values.map((v) => v.value)];
            const filteredValues = mappedValues.filter((v) => v !== newValue);
            const newValues = formatOnChange(filteredValues, fieldValue);
            onChange(newValues);
          } else {
            const mappedValues = [...values.map((v) => v.value), newValue];
            const newValues = formatOnChange(mappedValues, fieldValue);
            onChange(newValues);
          }
        };

        return (
          <HStack
            key={`${option.label}${option.value}`}
            bgColor={selected ? 'surface.backing-1' : undefined}
            width="100%"
            height="40px"
            px={2}
            py={1}
            justifyContent="space-between"
            alignItems="center"
            onClick={handleChange}
          >
            <HStack alignItems="center" gap={1}>
              <Label size="m">{option.label}</Label>
              {renderTag(option)}
            </HStack>

            {selected && (
              <Icon name="check" iconSize="20px" variant="custom" color="state.teal.secondary" />
            )}
          </HStack>
        );
      })}
      <ErrorMessage
        name={name}
        errors={parsedErrors}
        render={({ message }) => (
          <Text size="s" color="state.error.secondary">
            {message}
          </Text>
        )}
      />
    </Box>
  );
};

export default InternalMultiSelectList;
